import React from 'react';
import { useIntl } from 'react-intl';
import { MUSIC_VIDEO_SIZE_PX } from 'fym-shared/src/music24/musicVideoLook';
import { config } from '../../../config/config';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { Music24Logo } from '../../music24/components/Music24Logo';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
import { formatTime } from '../timeline/timelineGeometry';
import { CutMenu } from '../topbar/CutMenu';
import { UndoRedo } from '../topbar/UndoRedo';
import { useRequiredMusicVideoShowcase } from './MusicVideoShowcaseContext';
import { showcaseMessages } from './showcaseMessages';
import { ShowcaseSignUpLink } from './ShowcaseSignUpLink';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import editorStyles from '../MusicVideoEditor.module.scss';
import styles from './MusicVideoShowcase.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The top bar of the showcase (design c1): the "Demo" chip, the song, Undo and Redo, the cut menu
 * (its "New cut" and "Copy this cut" open the sign-up card), "Changes stay in this tab", "Watch
 * full screen", "Make the video" (the card) and "Sign up free". No Back, no save state, no plan and
 * no credits. The full page shows the Music24 logo, linked to music24.com; the embed does not.
 */
const ShowcaseTopBarComponent: React.FC<{ onWatch: () => void }> = ({ onWatch }) => {
  const intl = useIntl();
  const { data, doc, showUpsell } = useMusicVideoEditor();
  const { isEmbed } = useRequiredMusicVideoShowcase();
  const { song } = data;
  const size = MUSIC_VIDEO_SIZE_PX;

  return (
    <header className={editorStyles.top}>
      {!isEmbed && (
        <a
          className={styles.homeLink}
          href={config.smartLinksWebURL}
          aria-label={intl.formatMessage(showcaseMessages.homeLink)}
        >
          <Music24Logo size={22} />
        </a>
      )}
      <span className={styles.demoChip}>{intl.formatMessage(showcaseMessages.demoChip)}</span>
      <div className={editorStyles.song}>
        {song.artworkUrl !== null && <img src={song.artworkUrl} alt="" />}
        <div className={editorStyles.songText}>
          <b>{intl.formatMessage(showcaseMessages.songTitle, { artist: song.artistName, title: song.title })}</b>
          <small>
            {intl.formatMessage(showcaseMessages.songLine, {
              length: formatTime(doc.settings.endSec - doc.settings.startSec),
              size: `${size.width} × ${size.height}`,
            })}
          </small>
        </div>
      </div>
      <span className={editorStyles.topSeparator} />
      <UndoRedo />
      <CutMenu onNewCut={() => showUpsell('newCut')} onCopyCut={() => showUpsell('copyCut')} isBusy={false} />
      <span className={editorStyles.grow} />
      <span className={styles.kept}>{intl.formatMessage(showcaseMessages.kept)}</span>
      <SMLButton size="sm" outline omitCap onClick={onWatch}>
        {intl.formatMessage(editorMessages.watch)}
      </SMLButton>
      <SMLButton size="sm" outline omitCap onClick={() => showUpsell('make')}>
        {intl.formatMessage(showcaseMessages.make)}
      </SMLButton>
      <ShowcaseSignUpLink place="topBar">{intl.formatMessage(showcaseMessages.signUpFree)}</ShowcaseSignUpLink>
    </header>
  );
};

export const ShowcaseTopBar = TypedMemo(ShowcaseTopBarComponent);
